import { type Listing } from '@kijiji/generated/graphql-types'
import ExpandIcon from '@kijiji/icons/src/icons/Expand'
import { type FC } from 'react'

import { ListingImageType } from '@/features/listing/types/listings'
import {
  ListingGalleryCountBadge,
  ListingGalleryImage,
  ListingGalleryMainImageButton,
  ListingGalleryMainImageContainer,
  ListingGalleryVirtualTourBadgeWrapper,
  StyledPlayIcon,
} from '@/features/listing-gallery/components/styled'
import { VirtualTour } from '@/features/listing-gallery/components/VirtualTour'
import { VirtualTourBadge } from '@/features/listing-gallery/components/VirtualTourBadge'
import { getGalleryImages } from '@/features/listing-gallery/utils/getGalleryImages'
import { BodyText } from '@/ui/atoms/body-text'

export type ListingGalleryMainImageProps = {
  handleClick?: (index: number, type: ListingImageType) => void
  imageUrls?: Listing['imageUrls']
  imageAlt?: string
  /**
   * The ID of a youtube video. The parameters after ?v= in the youtube url.
   */
  youtubeVideoId?: string | null
  /**
   * The URL of a 360 virtual tour.
   */
  virtualTourUrl?: string | null
  /**
   * Defines if the badge counting how many items should be displayed
   */
  showImageBadge?: boolean
  /** Use the gallery image UI as a div instead of a interactive button */
  notInteractive?: boolean
}

export const ListingGalleryMainImage: FC<ListingGalleryMainImageProps> = ({
  handleClick,
  imageAlt,
  imageUrls = [],
  showImageBadge,
  youtubeVideoId,
  virtualTourUrl,
  notInteractive,
}) => {
  const { main } = getGalleryImages(imageUrls, youtubeVideoId, virtualTourUrl, 0)

  if (!main.imageSrc) return null

  const handleMainImageClick = () => {
    let mainImageType: ListingImageType = ListingImageType.IMAGE

    if (main.isVideo) {
      mainImageType = ListingImageType.VIDEO
    } else if (main.isVirtualTour) {
      mainImageType = ListingImageType.TOUR
    }

    /** Main image index will always be 0 */
    handleClick?.(0, mainImageType)
  }

  const handleVirtualTourBadgeClick = () => {
    handleClick?.(imageUrls.length, ListingImageType.TOUR)
  }

  const hasMoreThanOneImage = imageUrls.length > 1

  return (
    <ListingGalleryMainImageContainer>
      <ListingGalleryMainImageButton
        onClick={handleMainImageClick}
        imageSrc={main.imageSrc}
        data-testid="listing-gallery-main"
        as={notInteractive ? 'div' : 'button'}
        notInteractive={notInteractive}
      >
        {main.isVideo ? <StyledPlayIcon variant="md" data-testid="gallery-main-play-icon" /> : null}
        {main.isVirtualTour ? (
          <VirtualTour url={main.imageSrc} />
        ) : (
          <ListingGalleryImage
            src={main.imageSrc}
            alt={imageAlt}
            data-testid="gallery-main-image"
          />
        )}

        {showImageBadge ? (
          <ListingGalleryCountBadge hasMoreThanOneImage={hasMoreThanOneImage}>
            {hasMoreThanOneImage ? (
              <BodyText size="small">1 / {imageUrls.length}</BodyText>
            ) : (
              <ExpandIcon aria-hidden data-testid="gallery-main-expand-icon" />
            )}
          </ListingGalleryCountBadge>
        ) : null}
      </ListingGalleryMainImageButton>
      {/*  
          The virtual tour badge is displayed on the top left corner if it's mobile or if theres only one image.
          Context: It needs to be outside the `ListingGalleryMainContainer` because it's a button and nested buttons can cause hydration issues (plus A11Y issues)
        */}
      {virtualTourUrl && !main.isVirtualTour ? (
        <ListingGalleryVirtualTourBadgeWrapper hasMoreThanOneImage={hasMoreThanOneImage}>
          <VirtualTourBadge onClick={handleVirtualTourBadgeClick} />
        </ListingGalleryVirtualTourBadgeWrapper>
      ) : null}
    </ListingGalleryMainImageContainer>
  )
}
